import request from "@/lib/api/handler";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { QueryKeys } from "../utils";
import { IExperience, Role } from "@/redux/features/candidateProfileSlice";
import {
  CandidateApplicationStatus,
  CandidateWaitingRoomStatus,
  JobOpeningStatus,
} from "@/types";

export interface AdminPipelineJobOpening {
  id: string;
  role?: string;
  jobStatus: JobOpeningStatus;
  candidateStatus: CandidateApplicationStatus;
  companyName?: string;
  logoLink?: string;
}

export type AdminPipelineSummary = Record<CandidateWaitingRoomStatus, number>;

export interface AdminPipelineCandidate {
  id: string;
  userId: string;
  candidateId: string;
  attioId?: string;
  name?: string;
  email: string;
  stage: CandidateWaitingRoomStatus;
  profilePictureLink?: string;
  titleAndEmployer?: string;
  yearsOfExperience?: number;
  yearsOfHealthcareExperience?: number;
  isPublic: boolean;
  location?: string;
  biography?: string;
  role?: Role;
  linkedIn?: string;
  phoneNumber?: string;
  personalWebsite?: string;
  resumeLink?: string;
  retainerHourly?: string;
  retainerMonthly?: string;
  hasFundraisingExperience?: boolean;
  experiences: IExperience[];
  jobOpenings: AdminPipelineJobOpening[];
  notes?: string;
  memberSince?: string;
  updatedAt: string;
  stageUpdatedAt: string;
}

export const useAdminCandidatePipelineQuery = () => {
  const { getAccessTokenSilently } = useAuth0();
  const {
    data: adminCandidatePipeline,
    error: adminCandidatePipelineError,
    isLoading: adminCandidatePipelineLoading,
  } = useQuery({
    queryKey: QueryKeys.adminCandidatePipeline,
    enabled: true,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    async queryFn() {
      const token = await getAccessTokenSilently();
      return await request<{
        candidates: AdminPipelineCandidate[];
        summary: AdminPipelineSummary;
      }>({
        method: "GET",
        path: "/api/admin/candidate-pipeline",
        token,
      });
    },
  });

  return {
    adminCandidatePipeline,
    adminCandidatePipelineError,
    adminCandidatePipelineLoading,
  };
};
